import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { fetchLocations } from './client'
import type { LatLng, PublicLocation } from './types'

/**
 * FindParkingBrowser — the shared no-QR "find parking near me" browse surface.
 *
 * Extracted from the account portal's FindParking screen so the consumer
 * checkout's no-zone screen can reuse the exact, battle-tested map (Google map
 * with a schematic fallback), nearby-zone list, in-place search, and
 * tap-to-locate geolocation. The portal keeps its auth chrome (login slide-over,
 * signed-in status) OUTSIDE this component; this owns only the browse experience.
 *
 * Data is the public GET /api/locations directory (opt-in zones, display-safe
 * fields). Zones without lat/lng list without a map pin; the map renders only
 * when at least one location is geocoded. Distances show only after the visitor
 * taps "near me" and grants browser location — no permission prompt on load.
 *
 * Handoff to checkout is plain URL navigation: `{payBase}/?z=<zoneId>` to start
 * a session and `{payBase}/subscribe?z=<zoneId>` for a monthly permit. The
 * portal passes payBase='https://pay.parkify.com'; the consumer passes '' so the
 * links stay same-origin. `onSelectZone`, when given, overrides the start-parking
 * navigation entirely.
 */

export interface FindParkingBrowserProps {
  /**
   * Base URL of the pay app for the checkout handoff. Portal:
   * 'https://pay.parkify.com'. Consumer: '' (same-origin). Default is the
   * portal's target.
   */
  payBase?: string
  /**
   * Base URL of the public API for GET /api/locations. Portal: its deployed
   * Worker origin. Consumer: '' (same-origin / dev stub). Ignored when
   * `locations` is supplied.
   */
  apiBase?: string
  /**
   * Google Maps JS API key. When empty/undefined, or if the SDK fails to load,
   * the map degrades to the hand-drawn schematic. Each host passes
   * import.meta.env.VITE_GOOGLE_MAPS_API_KEY.
   */
  mapsApiKey?: string
  /**
   * Pre-fetched locations. When omitted, the component fetches from
   * `{apiBase}/api/locations` itself and manages its own loading/error state.
   */
  locations?: PublicLocation[]
  /**
   * Overrides the "Start parking here" action. Default: navigate to
   * `{payBase}/?z=<zoneId>` via an anchor.
   */
  onSelectZone?: (zoneId: string) => void
  /**
   * Render flush (no card wrapper) for hosts that already provide a card shell
   * (the consumer checkout wraps everything in its own `.pk-card`). Default
   * false — the portal renders the list/detail inside a self-contained card.
   */
  embedded?: boolean
}

const DEFAULT_PAY_BASE = 'https://pay.parkify.com'

// Schematic-map palette — brand-harmonized "palette A": a soft lilac land base,
// lavender parks (NO green, per brand policy), muted periwinkle water, and white
// roads with a purple-gray casing. Keeps the map legible while reading as
// Parkify rather than a generic Google map. Mirrored by MAP_STYLES below so the
// real Google tiles and this schematic fallback look the same.
const MAP_LAND = '#F4F1F8'
const MAP_PARK = '#E9E2F1'
const MAP_WATER = '#DDE0EF'
const MAP_ROAD = '#FFFFFF'
const MAP_CASING = '#E4DDEC'

function centsToUsd(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`
}

function rateLabel(rate: NonNullable<PublicLocation['rate']>): string {
  return rate.basis === 'hour' ? `${centsToUsd(rate.startingCents)}/hr` : `${centsToUsd(rate.startingCents)} flat`
}

function kindLabel(kind: PublicLocation['kind']): string | null {
  if (kind === 'garage') return 'Covered garage'
  if (kind === 'surface_lot') return 'Surface lot'
  return null
}

function streetOnly(address: string): string {
  return address.split(',')[0]!.trim()
}

function placeLine(l: PublicLocation): string | null {
  const street = l.address ? streetOnly(l.address) : null
  const cityState = [l.city, l.state].filter(Boolean).join(', ')
  if (street && cityState) return `${street}, ${cityState}`
  return street ?? (cityState || null)
}

// ── geodesy ──────────────────────────────────────────────────────────────────

// How far a lot can be and still count as "near me" when framing the map after
// a locate. Wide enough to keep the next lot over in frame, tight enough that a
// lot in another metro can't drag the viewport out to state level.
const NEAR_MILES = 8

function milesBetween(a: LatLng, b: LatLng): number {
  const R = 3958.8 // earth radius, miles
  const rad = (d: number) => (d * Math.PI) / 180
  const dLat = rad(b.lat - a.lat)
  const dLng = rad(b.lng - a.lng)
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

function distanceLabel(miles: number): string {
  if (miles < 10) return `${miles.toFixed(1)} mi`
  return `${Math.round(miles)} mi`
}

function hasCoords(l: PublicLocation): l is PublicLocation & LatLng {
  return typeof l.lat === 'number' && typeof l.lng === 'number'
}

// How far another lot can be and still be a real alternative to the one you just
// tapped — i.e. close enough to walk to the same destination from. Deliberately
// much tighter than NEAR_MILES: that one frames a map around a visitor, this one
// answers "if this lot doesn't suit me, what else serves the same block?".
const WALKABLE_MILES = 0.5

// Zoom level to settle on when a lot is picked. 16 is the "few blocks" frame —
// you can see the cross-streets and what the lot sits between, which is what the
// question "is this the right one?" actually needs. 15 is a whole district, 17 is
// one building.
const SELECT_ZOOM = 16

// Other lots worth offering alongside a selected one, nearest first. Returns the
// walkable set when there is one; otherwise the closest few at any distance,
// which the caller labels differently — a lot 34 miles away is a real answer to
// "where else can I park", but it is not a NEARBY one, and the heading has to
// say which of the two it is showing.
function alternativesTo(
  sel: PublicLocation,
  all: PublicLocation[],
  limit = 3,
): { list: PublicLocation[]; walkable: boolean } {
  if (!hasCoords(sel)) return { list: [], walkable: false }
  const others = all
    .filter((l) => l.id !== sel.id && hasCoords(l))
    .map((l) => ({ l, mi: milesBetween(sel, l as PublicLocation & LatLng) }))
    .sort((a, b) => a.mi - b.mi)
  const walkable = others.filter((o) => o.mi <= WALKABLE_MILES)
  if (walkable.length > 0) return { list: walkable.map((o) => o.l), walkable: true }
  return { list: others.slice(0, limit).map((o) => o.l), walkable: false }
}

// The lots close enough to belong in the same frame as the visitor. Both maps
// use this to zoom TO the user rather than to the whole pin set.
function nearbyTo(loc: LatLng, pins: Array<PublicLocation & LatLng>): Array<PublicLocation & LatLng> {
  return pins.filter((l) => milesBetween(loc, l) <= NEAR_MILES)
}

// ── icons ────────────────────────────────────────────────────────────────────

const SearchSvg = ({ c = 'var(--color-brand)', s = 20 }: { c?: string; s?: number }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
  </svg>
)

const LocateSvg = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="4" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
  </svg>
)

const PinRowSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
)

const ChevR = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-gray-500)"
    strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 6l6 6-6 6" />
  </svg>
)

const MapPin = ({ sel }: { sel: boolean }) => (
  <svg width={sel ? 38 : 30} height={sel ? 46 : 37} viewBox="0 0 32 40"
    style={{ filter: 'drop-shadow(0 3px 4px rgba(0,0,0,.28))', display: 'block' }} aria-hidden>
    <path d="M16 0C7.2 0 0 7 0 15.6 0 26.4 16 40 16 40s16-13.6 16-24.4C32 7 24.8 0 16 0z"
      fill={sel ? 'var(--color-brand-dark)' : 'var(--color-brand)'} />
    <circle cx="16" cy="15.6" r="8.6" fill="#fff" />
    <text x="16" y="20" textAnchor="middle" fontSize="13" fontWeight="800"
      fill={sel ? 'var(--color-brand-dark)' : 'var(--color-brand)'}>P</text>
  </svg>
)

// ── schematic map ────────────────────────────────────────────────────────────
//
// Not real tiles — a styled backdrop with pins PROJECTED from the locations'
// real lat/lng (equirectangular over the padded bounding box of every pinned
// location + the visitor, when known). Right for a handful of lots; swap for
// a tile library only when density demands it.

const MAP_W = 346
const MAP_H = 262

function project(points: LatLng[], p: LatLng): { xPct: number; yPct: number } {
  const lats = points.map((q) => q.lat)
  const lngs = points.map((q) => q.lng)
  // Guard tiny/zero spans (a single pin, or co-located pins) with a floor so
  // the divide below never explodes; ~0.008° ≈ half a mile.
  const spanLat = Math.max(Math.max(...lats) - Math.min(...lats), 0.008)
  const spanLng = Math.max(Math.max(...lngs) - Math.min(...lngs), 0.008)
  const midLat = (Math.max(...lats) + Math.min(...lats)) / 2
  const midLng = (Math.max(...lngs) + Math.min(...lngs)) / 2
  // 30% padding around the bounding box keeps pins off the edges.
  const x = (p.lng - (midLng - spanLng * 0.65)) / (spanLng * 1.3)
  const y = ((midLat + spanLat * 0.65) - p.lat) / (spanLat * 1.3)
  return { xPct: Math.min(96, Math.max(4, x * 100)), yPct: Math.min(94, Math.max(10, y * 100)) }
}

interface MapProps {
  pinned: Array<PublicLocation & LatLng>
  userLoc: LatLng | null
  selId: string | null
  onPick: (l: PublicLocation) => void
  onLocate: () => void
  locating: boolean
  mapsApiKey?: string
}

function LocateButton({ onLocate, locating }: { onLocate: () => void; locating: boolean }) {
  return (
    <button type="button" className="pkfp-map__locate" onClick={onLocate} aria-label="Use my location" disabled={locating}>
      {locating
        ? <span className="pkfp-spinner" style={{ borderColor: 'var(--color-brand-light)', borderTopColor: 'var(--color-brand)' }} />
        : <LocateSvg />}
    </button>
  )
}

function SchematicMap({ pinned, userLoc, selId, onPick, onLocate, locating }: MapProps) {
  // Same "frame the user, not the whole country" rule as the Google map. The
  // projection spans the bounding box of whatever we hand it, so including
  // every pin nationwide squeezed the visitor's own block down to a few pixels
  // — locate looked like it did nothing. Frame the user plus only the nearby
  // lots, and show only those: this map has no viewport to scroll off, so a
  // far-away lot would otherwise be clamped onto the border as if it were
  // right there. The list below still carries every location, distance-sorted.
  const near = userLoc ? nearbyTo(userLoc, pinned) : []
  // Nothing nearby ⇒ keep the all-pins framing; the user's dot clamps to an
  // edge, which honestly reads as "you're off this map".
  const shown = userLoc && near.length ? near : pinned
  const frame: LatLng[] = userLoc ? [...shown, userLoc] : [...shown]
  const user = userLoc ? project(frame, userLoc) : null
  return (
    <div className="pkfp-map">
      <svg width="100%" height="100%" viewBox={`0 0 ${MAP_W} ${MAP_H}`} preserveAspectRatio="xMidYMid slice"
        style={{ position: 'absolute', inset: 0, background: MAP_LAND }} aria-hidden>
        <path d="M-20,-10 L96,-10 C60,40 36,60 -20,74 Z" fill={MAP_WATER} opacity="0.5" />
        <path d="M-20,30 C50,15 95,45 110,80 C120,115 80,135 40,124 C-5,112 -40,80 -20,30 Z" fill={MAP_PARK} />
        <path d="M268,150 C318,138 358,176 348,224 C340,262 296,262 268,224 Z" fill={MAP_PARK} />
        <path d="M-20,224 C30,212 70,244 60,300 L-20,300 Z" fill={MAP_PARK} />
        <g stroke={MAP_CASING} fill="none" strokeLinecap="round">
          <path d="M250,-10 C242,90 222,180 175,300" strokeWidth="17" />
          <path d="M-10,150 C80,128 200,140 360,128" strokeWidth="15" />
          <path d="M120,-10 C112,90 110,180 100,300" strokeWidth="9" />
          <path d="M-10,210 C90,196 200,210 300,184" strokeWidth="9" />
        </g>
        <g stroke={MAP_ROAD} fill="none" strokeLinecap="round">
          <path d="M250,-10 C242,90 222,180 175,300" strokeWidth="11" />
          <path d="M-10,150 C80,128 200,140 360,128" strokeWidth="9" />
          <path d="M120,-10 C112,90 110,180 100,300" strokeWidth="5" />
          <path d="M-10,210 C90,196 200,210 300,184" strokeWidth="5" />
        </g>
      </svg>
      {user && (
        <div style={{ position: 'absolute', left: `${user.xPct}%`, top: `${user.yPct}%`, transform: 'translate(-50%,-50%)' }}>
          <div style={{
            width: 16, height: 16, borderRadius: '50%', background: '#2D8CFF', border: '3px solid #fff',
            boxShadow: '0 0 0 6px rgba(45,140,255,.16), 0 1px 4px rgba(0,0,0,.3)',
          }} />
        </div>
      )}
      {shown.map((l) => {
        const pos = project(frame, l)
        const sel = selId === l.id
        return (
          <button
            key={l.id}
            type="button"
            onClick={() => onPick(l)}
            aria-label={l.name}
            style={{
              position: 'absolute', left: `${pos.xPct}%`, top: `${pos.yPct}%`,
              transform: 'translate(-50%,-100%)', padding: 0, lineHeight: 0,
              zIndex: sel ? 3 : 2,
            }}
          >
            <MapPin sel={sel} />
          </button>
        )
      })}
      <LocateButton onLocate={onLocate} locating={locating} />
    </div>
  )
}

// ── real map (Google Maps) ─────────────────────────────────────────────────────
//
// Upgrade from the schematic when a Maps key is set: real tiles with street
// names + native pan/zoom. Headless-free — this DOES render a map, so the host
// CSP must allow maps.googleapis.com/maps.gstatic.com (script+connect), blob:
// workers, and 'wasm-unsafe-eval' for the WebGL renderer. On any load failure we
// fall back to <SchematicMap> so the browse page never shows a blank box. Pins
// reuse the brand purple; the selected pin is larger + darker.

const PIN_PURPLE = '#7F3F98'
const PIN_DARK = '#5C2E70'

// Brand map styling — retints Google's live tiles to Parkify's "palette A" so the
// real map matches the SchematicMap palette above (and the rest of the checkout):
// lilac land, lavender parks (NO green, per brand policy), muted water, white
// roads with a soft purple-gray casing, and quieted POI/transit clutter so the
// purple pins lead. Real streets, names, and pan/zoom stay intact — only the
// palette shifts. Type-only reference to google.maps, erased at runtime.
const MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: MAP_LAND }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#6E6678' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: MAP_LAND }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#E0D8EA' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#EDE7F3' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: MAP_PARK }] },
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: MAP_PARK }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: MAP_ROAD }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: MAP_CASING }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8A8291' }] },
  { featureType: 'road.local', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#FBFAFD' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#E0D8EA' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: MAP_WATER }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9AA0BD' }] },
]

function pinDataUri(color: string): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='40' viewBox='0 0 32 40'>`
    + `<path d='M16 0C7.2 0 0 7 0 15.6 0 26.4 16 40 16 40s16-13.6 16-24.4C32 7 24.8 0 16 0z' fill='${color}'/>`
    + `<circle cx='16' cy='15.6' r='8.6' fill='#ffffff'/>`
    + `<text x='16' y='20' text-anchor='middle' font-size='13' font-weight='800' fill='${color}' font-family='Arial,Helvetica,sans-serif'>P</text></svg>`
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

function userDotUri(): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 22 22'>`
    + `<circle cx='11' cy='11' r='7' fill='#2D8CFF' stroke='#ffffff' stroke-width='3'/></svg>`
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

// Google calls window.gm_authFailure on an auth failure (invalid key, referrer
// not on the key's allowlist, billing off). Critically this fires ASYNC AFTER
// the map is constructed — outside the init effect's try/catch — so without
// handling it the map div shows Google's "Oops! Something went wrong" box
// instead of our schematic. Latch the failure and notify every mounted map so
// they drop to the schematic fallback.
let mapsAuthFailed = false
const authFailureListeners = new Set<() => void>()
function installAuthFailureHook(): void {
  const w = window as unknown as { gm_authFailure?: () => void }
  if (typeof w.gm_authFailure === 'function') return
  w.gm_authFailure = () => {
    mapsAuthFailed = true
    authFailureListeners.forEach((fn) => fn())
  }
}

// Singleton loader (mirrors ops AddressAutocomplete): one <script> for the whole
// app, resolves when google.maps.importLibrary is ready.
let mapsLoader: Promise<void> | null = null
function loadMaps(apiKey: string): Promise<void> {
  installAuthFailureHook()
  const w = window as unknown as { google?: { maps?: { importLibrary?: unknown } } }
  const ready = (): boolean => typeof w.google?.maps?.importLibrary === 'function'
  if (ready()) return Promise.resolve()
  if (!apiKey) return Promise.reject(new Error('Maps key not configured'))
  if (!mapsLoader) {
    mapsLoader = new Promise<void>((resolve, reject) => {
      // The <script> onload can fire a beat BEFORE google.maps.importLibrary is
      // assigned (loading=async), so resolving on onload alone races and the
      // caller's importLibrary('maps') throws "not a function". Poll for
      // readiness after load instead of trusting onload.
      const waitForReady = (deadline: number): void => {
        if (ready()) { resolve(); return }
        if (Date.now() > deadline) {
          mapsLoader = null
          reject(new Error('Google Maps loaded but importLibrary never became available'))
          return
        }
        setTimeout(() => waitForReady(deadline), 40)
      }
      const s = document.createElement('script')
      s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`
        + '&v=weekly&loading=async&libraries=maps,marker'
      s.async = true
      s.onload = () => waitForReady(Date.now() + 8000)
      s.onerror = () => { mapsLoader = null; reject(new Error('Google Maps failed to load')) }
      document.head.appendChild(s)
    })
  }
  return mapsLoader
}

function GoogleMapView(props: MapProps) {
  const { pinned, userLoc, selId, onPick, onLocate, locating, mapsApiKey } = props
  const elRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map())
  const userMarkerRef = useRef<google.maps.Marker | null>(null)
  // Class constructors as RETURNED by importLibrary. Critical with
  // loading=async: the classes are NOT reliably attached to the google.maps.*
  // namespace at init time, so referencing google.maps.Size/Marker/etc. there
  // can throw and silently drop us to the schematic. Use the importLibrary
  // return values instead. Cached so the selId/userLoc effects can build too.
  const libsRef = useRef<{
    Marker: typeof google.maps.Marker
    Size: typeof google.maps.Size
    Point: typeof google.maps.Point
    LatLngBounds: typeof google.maps.LatLngBounds
    event: typeof google.maps.event
    ControlPosition: typeof google.maps.ControlPosition
  } | null>(null)
  // Latest onPick without re-running the init effect (which builds markers once).
  const pickRef = useRef(onPick)
  pickRef.current = onPick
  // Latest userLoc for the init effect (which runs once, after the SDK loads) —
  // so a "near me" tap that lands WHILE the map is still loading isn't dropped.
  const userLocRef = useRef(userLoc)
  userLocRef.current = userLoc
  // Seed from the module latch so a remount after a known auth failure goes
  // straight to the schematic without flashing Google's error box.
  const [failed, setFailed] = useState(mapsAuthFailed)

  // Fall back to the schematic if Google reports an auth failure (bad key /
  // referrer / billing) — which arrives async, after the map is built.
  useEffect(() => {
    const onAuthFail = () => setFailed(true)
    authFailureListeners.add(onAuthFail)
    return () => { authFailureListeners.delete(onAuthFail) }
  }, [])

  const iconFor = (sel: boolean): google.maps.Icon => {
    const libs = libsRef.current!
    const w = sel ? 38 : 30
    const h = sel ? 46 : 37
    return {
      url: pinDataUri(sel ? PIN_DARK : PIN_PURPLE),
      scaledSize: new libs.Size(w, h),
      anchor: new libs.Point(w / 2, h),
    }
  }

  // Fit the viewport to the pins (+ user dot), clamping a single/clustered fit
  // so it doesn't slam to building-level zoom.
  const fit = (map: google.maps.Map, loc: LatLng | null): void => {
    const libs = libsRef.current!
    const bounds = new libs.LatLngBounds()
    pinned.forEach((l) => bounds.extend({ lat: l.lat, lng: l.lng }))
    if (loc) bounds.extend(loc)
    if (bounds.isEmpty()) return
    map.fitBounds(bounds, 56)
    libs.event.addListenerOnce(map, 'idle', () => {
      const z = map.getZoom()
      if (typeof z === 'number' && z > 15) map.setZoom(15)
    })
  }

  // Zoom TO the user, not to every pin in the country. Fitting the full pin set
  // once location lands is what made "locate me" appear to do nothing but show
  // the whole US: with lots far apart, the union bounds is the whole footprint.
  // Fit only the pins near the user, and fall back to a plain centered zoom when
  // nothing is nearby.
  const focusUser = (map: google.maps.Map, loc: LatLng): void => {
    const libs = libsRef.current!
    const near = nearbyTo(loc, pinned)
    if (!near.length) {
      map.setCenter(loc)
      map.setZoom(14)
      return
    }
    const bounds = new libs.LatLngBounds()
    bounds.extend(loc)
    near.forEach((l) => bounds.extend({ lat: l.lat, lng: l.lng }))
    map.fitBounds(bounds, 56)
    libs.event.addListenerOnce(map, 'idle', () => {
      const z = map.getZoom()
      if (typeof z === 'number' && z > 15) map.setZoom(15)
    })
  }

  // Drop/update the blue user-location dot + refit. Shared by the init effect
  // and the userLoc effect so it works whether location lands before or after
  // the map finishes loading.
  const applyUserLoc = (map: google.maps.Map, loc: LatLng): void => {
    const libs = libsRef.current!
    if (userMarkerRef.current) {
      userMarkerRef.current.setPosition(loc)
    } else {
      userMarkerRef.current = new libs.Marker({
        position: loc,
        map,
        icon: { url: userDotUri(), scaledSize: new libs.Size(22, 22), anchor: new libs.Point(11, 11) },
        zIndex: 1,
        clickable: false,
      })
    }
    focusUser(map, loc)
  }

  // Init once: load the SDK, build the map + markers, fit to the pins.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        await loadMaps(mapsApiKey ?? '')
        const [mapsLib, markerLib, coreLib] = await Promise.all([
          google.maps.importLibrary('maps') as Promise<google.maps.MapsLibrary>,
          google.maps.importLibrary('marker') as Promise<google.maps.MarkerLibrary>,
          google.maps.importLibrary('core') as Promise<google.maps.CoreLibrary>,
        ])
        if (cancelled || !elRef.current) return
        libsRef.current = {
          Marker: markerLib.Marker,
          Size: coreLib.Size,
          Point: coreLib.Point,
          LatLngBounds: coreLib.LatLngBounds,
          event: coreLib.event,
          ControlPosition: coreLib.ControlPosition,
        }
        const map = new mapsLib.Map(elRef.current, {
          center: { lat: pinned[0]!.lat, lng: pinned[0]!.lng },
          zoom: 14,
          styles: MAP_STYLES,
          disableDefaultUI: true,
          zoomControl: true,
          // Google's zoom control defaults to the bottom-right — the same corner
          // as our locate button, which stacked the two on top of each other.
          zoomControlOptions: { position: coreLib.ControlPosition.RIGHT_TOP },
          gestureHandling: 'cooperative', // ctrl/two-finger to zoom — never hijacks page scroll
          clickableIcons: false,
          keyboardShortcuts: false,
        })
        mapRef.current = map
        pinned.forEach((l) => {
          const m = new markerLib.Marker({
            position: { lat: l.lat, lng: l.lng },
            map,
            title: l.name,
            icon: iconFor(selId === l.id),
            zIndex: selId === l.id ? 3 : 2,
          })
          m.addListener('click', () => pickRef.current(l))
          markersRef.current.set(l.id, m)
        })
        // Use the freshest userLoc (a "near me" tap may have landed mid-load).
        if (userLocRef.current) applyUserLoc(map, userLocRef.current)
        else fit(map, null)
      } catch (e) {
        // Keep the schematic as the visible fallback; log so a fallback is
        // never silent/undiagnosable.
        console.warn('[find-map] Google Maps unavailable, using schematic:', e)
        if (!cancelled) setFailed(true)
      }
    })()
    return () => {
      cancelled = true
      markersRef.current.forEach((m) => m.setMap(null))
      markersRef.current.clear()
      userMarkerRef.current?.setMap(null)
      userMarkerRef.current = null
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Selection → swap the pin icons + pan to the chosen location.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !libsRef.current) return
    markersRef.current.forEach((m, id) => {
      const sel = id === selId
      m.setIcon(iconFor(sel))
      m.setZIndex(sel ? 3 : 2)
    })
    if (selId) {
      const l = pinned.find((p) => p.id === selId)
      if (l) {
        map.panTo({ lat: l.lat, lng: l.lng })
        // Pan alone left the viewport wherever the fit-all-pins framing put it.
        // With lots in different states that is zoom 3 — the whole country — so
        // tapping a lot moved nothing you could see and told you nothing about
        // where it actually is.
        //
        // Only ever zoom IN: someone who has already pushed past SELECT_ZOOM is
        // reading the block, and yanking them back out to 16 would undo that.
        const z = map.getZoom()
        if (typeof z !== 'number' || z < SELECT_ZOOM) map.setZoom(SELECT_ZOOM)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId])

  // User location → drop/update a blue dot and refit to include it.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !userLoc || !libsRef.current) return
    applyUserLoc(map, userLoc)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoc])

  if (failed) return <SchematicMap {...props} />
  return (
    <div className="pkfp-map">
      <div ref={elRef} style={{ position: 'absolute', inset: 0 }} role="application" aria-label="Map of nearby parking locations" />
      <LocateButton onLocate={onLocate} locating={locating} />
    </div>
  )
}

// Real Google map when a key is configured; otherwise (and on SDK-load failure,
// handled inside GoogleMapView) the hand-drawn schematic.
function LocationMap(props: MapProps) {
  return props.mapsApiKey ? <GoogleMapView {...props} /> : <SchematicMap {...props} />
}

// ── list & detail ────────────────────────────────────────────────────────────

function SecLbl({ children }: { children: ReactNode }) {
  return <div className="pkfp-seclbl" style={{ margin: '18px 0 8px' }}><span>{children}</span></div>
}

function LocationRow({
  loc, miles, onPick,
}: {
  loc: PublicLocation
  miles: number | null
  onPick: () => void
}) {
  const bits: string[] = []
  if (miles !== null) bits.push(distanceLabel(miles))
  if (loc.rate) bits.push(`from ${rateLabel(loc.rate)}`)
  if (loc.monthly) bits.push('Monthly available')
  return (
    <button type="button" className="pkfp-lotrow" onClick={onPick}>
      <span className="pkfp-row-icon"><PinRowSvg /></span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: 'block', fontSize: 15, fontWeight: 800, color: 'var(--color-black)',
          letterSpacing: '-0.2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{loc.name}</span>
        <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--color-gray-600)', marginTop: 1 }}>
          {bits.length > 0 ? bits.join(' · ') : (placeLine(loc) ?? '')}
        </span>
      </span>
      <ChevR />
    </button>
  )
}

function LocationDetail({
  loc, miles, onBack, payBase, onSelectZone,
}: {
  loc: PublicLocation
  miles: number | null
  onBack: () => void
  payBase: string
  onSelectZone?: (zoneId: string) => void
}) {
  const kind = kindLabel(loc.kind)
  const startUrl = `${payBase}/?z=${encodeURIComponent(loc.id)}`
  const permitUrl = `${payBase}/subscribe?z=${encodeURIComponent(loc.id)}`
  return (
    <div className="pkfp-fadein">
      <button type="button" onClick={onBack}
        style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12, color: 'var(--color-black)' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M15 6l-6 6 6 6" />
        </svg>
        <span style={{ fontSize: 13.5, fontWeight: 700 }}>All locations</span>
      </button>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--color-black)', letterSpacing: '-0.4px', lineHeight: 1.15 }}>
        {loc.name}
      </h2>
      <div style={{
        fontSize: 13, color: 'var(--color-gray-600)', fontWeight: 500, marginTop: 5,
        display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap',
      }}>
        {placeLine(loc) && <span>{placeLine(loc)}</span>}
        {miles !== null && <><span>·</span><span>{distanceLabel(miles)} away</span></>}
        {kind && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', background: 'var(--color-gray-200)',
            borderRadius: 999, padding: '3px 9px', fontWeight: 700, color: 'var(--color-black)', fontSize: 12,
          }}>{kind}</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 16, marginBottom: 18 }}>
        <div className="pkfp-ratecard">
          <div className="pkfp-ratecard__lbl">STARTING AT</div>
          <div className="pkfp-ratecard__val">{loc.rate ? rateLabel(loc.rate) : '—'}</div>
          <div className="pkfp-ratecard__sub">{loc.rate ? 'rates may vary' : 'see checkout for rates'}</div>
        </div>
        <div className="pkfp-ratecard" style={{ opacity: loc.monthly ? 1 : 0.55 }}>
          <div className="pkfp-ratecard__lbl">SUBSCRIPTION</div>
          <div className="pkfp-ratecard__val">{loc.monthly ? `${centsToUsd(loc.monthly.fromCents)}/mo` : '—'}</div>
          <div className="pkfp-ratecard__sub">{loc.monthly ? 'unlimited in/out' : 'not offered here'}</div>
        </div>
      </div>
      {/* Hand-off moment: browsing lives here; paying lives on the pay app. */}
      {onSelectZone ? (
        <button type="button" className="pkfp-btn-primary" onClick={() => onSelectZone(loc.id)}
          style={{ width: '100%', marginBottom: 10 }}>
          Start parking here
        </button>
      ) : (
        <a className="pkfp-btn-primary" href={startUrl} style={{ width: '100%', marginBottom: 10, textDecoration: 'none' }}>
          Start parking here
        </a>
      )}
      {loc.monthly && (
        <a className="pkfp-btn-outline" href={permitUrl}>
          Buy subscription · {centsToUsd(loc.monthly.fromCents)}/mo
        </a>
      )}
    </div>
  )
}

// ── search (in-place) ────────────────────────────────────────────────────────

function SearchPanel({
  locations, milesFor, onClose, onPick, onNearMe, embedded,
}: {
  locations: PublicLocation[]
  milesFor: (l: PublicLocation) => number | null
  onClose: () => void
  onPick: (l: PublicLocation | null) => void
  onNearMe: () => void
  embedded: boolean
}) {
  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()
  const matches = q
    ? locations.filter((l) =>
        [l.name, l.address, l.city, l.state].filter(Boolean).join(' ').toLowerCase().includes(q))
    : locations
  return (
    <div className={embedded ? 'pkfp-fadein' : 'pkfp-card pkfp-fadein'} style={embedded ? undefined : { padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: 17, fontWeight: 800, color: 'var(--color-black)' }}>Find parking</span>
        <button type="button" onClick={onClose} aria-label="Close search" style={{ padding: 4, lineHeight: 0 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-black)"
            strokeWidth="2.4" strokeLinecap="round" aria-hidden>
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 11,
        border: '2px solid var(--color-black)', borderRadius: 13, padding: '13px 14px', marginBottom: 6,
      }}>
        <SearchSvg c="var(--color-black)" s={22} />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, address, or city"
          aria-label="Search locations"
          style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', fontSize: 16, fontWeight: 500, background: 'transparent', color: 'var(--color-black)' }}
        />
      </div>
      <button type="button" onClick={onNearMe}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 4px', width: '100%', textAlign: 'left', borderBottom: '1px solid var(--color-gray-300)' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="var(--color-brand)" stroke="var(--color-brand)"
          strokeWidth="1.5" strokeLinejoin="round" aria-hidden>
          <path d="M3 11l18-8-8 18-2-8z" />
        </svg>
        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-brand)' }}>Show parking near me</span>
      </button>
      <div>
        {matches.map((l) => (
          <LocationRow key={l.id} loc={l} miles={milesFor(l)} onPick={() => onPick(l)} />
        ))}
        {q && matches.length === 0 && (
          <div style={{ padding: '26px 12px', fontSize: 14, color: 'var(--color-gray-600)', fontWeight: 500, textAlign: 'center', lineHeight: 1.5 }}>
            No matching locations yet. Try a nearby address or city.
          </div>
        )}
      </div>
    </div>
  )
}

// ── shared panel wrapper ───────────────────────────────────────────────────────
// The portal renders each browse panel inside a self-contained card; the
// consumer embeds it inside its own checkout card, so drop the wrapper and
// render flush.
// `pkfp-panel` carries no styling of its own — it is the scope hook the
// package's button reset hangs off, so an embedded host (which gets no
// `pkfp-card`) still resets the bare buttons rendered inside.
function Panel({ embedded, style, children }: { embedded: boolean; style?: CSSProperties; children: ReactNode }) {
  return (
    <div className={embedded ? 'pkfp-panel' : 'pkfp-card pkfp-panel'} style={embedded ? style : { padding: 20, ...style }}>
      {children}
    </div>
  )
}

// ── browser ────────────────────────────────────────────────────────────────────

export function FindParkingBrowser({
  payBase = DEFAULT_PAY_BASE,
  apiBase = '',
  mapsApiKey,
  locations: providedLocations,
  onSelectZone,
  embedded = false,
}: FindParkingBrowserProps) {
  const [fetched, setFetched] = useState<PublicLocation[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reloadCount, setReloadCount] = useState(0)
  const [sel, setSel] = useState<PublicLocation | null>(null)
  const [searching, setSearching] = useState(false)
  const [userLoc, setUserLoc] = useState<LatLng | null>(null)
  const [locating, setLocating] = useState(false)
  const [locNote, setLocNote] = useState<string | null>(null)

  const locations = providedLocations ?? fetched

  useEffect(() => {
    // Caller supplies data — skip the internal fetch entirely.
    if (providedLocations) return
    let cancelled = false
    setError(null)
    void (async () => {
      try {
        const locs = await fetchLocations(apiBase)
        if (!cancelled) setFetched(locs)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Could not load locations.')
      }
    })()
    return () => { cancelled = true }
  }, [reloadCount, apiBase, providedLocations])

  const locate = () => {
    if (locating) return
    if (!('geolocation' in navigator)) {
      setLocNote("Location isn't available in this browser.")
      return
    }
    setLocating(true)
    setLocNote(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude })
        setLocating(false)
      },
      () => {
        setLocNote('We couldn’t get your location — distances are hidden.')
        setLocating(false)
      },
      { maximumAge: 60_000, timeout: 10_000 },
    )
  }

  const milesFor = (l: PublicLocation): number | null =>
    userLoc && hasCoords(l) ? milesBetween(userLoc, l) : null

  const sorted = useMemo(() => {
    if (!locations) return []
    if (!userLoc) return locations
    return [...locations].sort((a, b) => {
      const da = hasCoords(a) ? milesBetween(userLoc, a) : Number.POSITIVE_INFINITY
      const db = hasCoords(b) ? milesBetween(userLoc, b) : Number.POSITIVE_INFINITY
      return da - db
    })
  }, [locations, userLoc])

  const pinned = useMemo(() => (locations ?? []).filter(hasCoords), [locations])

  if (error) {
    return (
      <Panel embedded={embedded}>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>Something went wrong</h2>
        <p style={{ margin: '0 0 16px', color: 'var(--color-gray-700)' }}>{error}</p>
        <button type="button" className="pkfp-btn-primary" onClick={() => setReloadCount((n) => n + 1)}
          style={{ fontSize: 13, padding: '10px 18px' }}>
          Retry
        </button>
      </Panel>
    )
  }

  if (!locations) {
    return (
      <Panel embedded={embedded} style={{ textAlign: 'center', padding: '48px 28px' }}>
        <div style={{
          width: 32, height: 32,
          border: '3px solid var(--color-brand-light)',
          borderTopColor: 'var(--color-brand)',
          borderRadius: '50%', margin: '0 auto',
          animation: 'pkfp-spin 0.8s linear infinite',
        }} />
      </Panel>
    )
  }

  if (searching) {
    return (
      <SearchPanel
        locations={sorted}
        milesFor={milesFor}
        onClose={() => setSearching(false)}
        onPick={(l) => { setSel(l); setSearching(false) }}
        onNearMe={() => { setSearching(false); locate() }}
        embedded={embedded}
      />
    )
  }

  if (locations.length === 0) {
    return (
      <Panel embedded={embedded} style={{ textAlign: 'center', padding: '40px 24px' }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%', background: 'var(--color-brand-light)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
        }}><PinRowSvg /></div>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, letterSpacing: '-0.3px' }}>
          Location browsing is coming online
        </h2>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--color-gray-700)', lineHeight: 1.6 }}>
          We're adding parking locations here as they come aboard. On-site already?
          Scan the posted Parkify code to start in the exact zone.
        </p>
      </Panel>
    )
  }

  return (
    <div className="pkfp-fadein">
      <button type="button" className="pkfp-searchbtn" onClick={() => setSearching(true)}>
        <SearchSvg />
        <span style={{ fontSize: 15, color: 'var(--color-gray-600)', fontWeight: 600 }}>Search parking near you</span>
      </button>

      {pinned.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <LocationMap
            pinned={pinned}
            userLoc={userLoc}
            selId={sel?.id ?? null}
            onPick={(l) => setSel(l)}
            onLocate={locate}
            locating={locating}
            mapsApiKey={mapsApiKey}
          />
        </div>
      )}
      {locNote && (
        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-gray-600)', fontWeight: 500 }}>{locNote}</p>
      )}

      <Panel embedded={embedded}>
        {sel ? (
          <>
            <LocationDetail loc={sel} miles={milesFor(sel)} onBack={() => setSel(null)}
              payBase={payBase} onSelectZone={onSelectZone} />
            {/* Tapping a lot used to replace the whole list, so the other options
                vanished and the only way back to them was the Back button. Keep
                the alternatives on screen, scoped to the selection rather than to
                the visitor — you are now asking "what else serves THIS block?".
                The heading tells you which question was answerable: real walkable
                neighbours, or just the closest we have. Silently labelling a lot
                34 miles away "nearby" would be the easy version and a lie. */}
            {(() => {
              const { list, walkable } = alternativesTo(sel, sorted)
              if (list.length === 0) return null
              return (
                <div style={{ marginTop: 18 }}>
                  <SecLbl>{walkable ? 'Also nearby' : 'Nearest other locations'}</SecLbl>
                  {list.map((l) => (
                    <LocationRow
                      key={l.id}
                      loc={l}
                      miles={hasCoords(sel) && hasCoords(l) ? milesBetween(sel, l) : null}
                      onPick={() => setSel(l)}
                    />
                  ))}
                </div>
              )
            })()}
          </>
        ) : (
          <div>
            <SecLbl>Parking locations</SecLbl>
            {sorted.map((l) => (
              <LocationRow key={l.id} loc={l} miles={milesFor(l)} onPick={() => setSel(l)} />
            ))}
            <div style={{
              fontSize: 11.5, color: 'var(--color-gray-600)', fontWeight: 500,
              lineHeight: 1.45, textAlign: 'center', marginTop: 12,
            }}>
              On-site with a Parkify code? Scanning it starts you in the exact zone.
            </div>
          </div>
        )}
      </Panel>
    </div>
  )
}
